import type { ContactShareStateResponse } from '../api/routes';
import type { ContactShareState } from '../types';

const asBool = (value: unknown, fallback = false): boolean =>
  typeof value === 'boolean' ? value : fallback;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const asNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

export const toContactShareState = (
  raw: ContactShareStateResponse | null | undefined,
): ContactShareState | null => {
  if (!raw) {
    return null;
  }
  const viewerRole =
    raw.viewer_role === 'tutor' || raw.viewer_role === 'student' ? raw.viewer_role : null;

  const shared = asBool(raw.shared);
  const hasLeftReview = asBool(raw.has_left_review);
  const reviewEligibleNow =
    typeof raw.review_eligible_now === 'boolean' ? raw.review_eligible_now : shared;
  const canLeaveReview =
    typeof raw.can_leave_review === 'boolean'
      ? raw.can_leave_review
      : shared && !hasLeftReview;

  return {
    eligible: asBool(raw.eligible),
    shared,
    viewer_role: viewerRole,
    user_consented: asBool(raw.user_consented),
    other_consented: asBool(raw.other_consented),
    tutor_consented: asBool(raw.tutor_consented),
    student_consented: asBool(raw.student_consented),
    user_has_contacts: raw.user_has_contacts,
    missing_fields: raw.missing_fields ?? [],
    contacts_shared_at: asNullableString(raw.contacts_shared_at),
    review_window_ends_at: asNullableString(raw.review_window_ends_at),
    review_eligible_now: reviewEligibleNow,
    can_leave_review: canLeaveReview,
    has_left_review: hasLeftReview,
    deal_auto_closed: asBool(raw.deal_auto_closed),
    seconds_until_review_window: asNullableNumber(raw.seconds_until_review_window),
  };
};

export const isContactShareMessageText = (text: string | undefined): boolean => {
  if (!text) {
    return false;
  }
  try {
    const parsed = JSON.parse(text) as { kind?: string };
    return parsed?.kind === 'contact_share';
  } catch {
    return false;
  }
};
